/**
 * Scanning the code signs the phone in.
 *
 * Without this the tunnel works perfectly and nothing in it does: every call
 * arrives at a backend that has never heard of this phone and is refused.
 * That is what "Failed to create new chat" was on 26 July, and it was not
 * only the new chat: every single call was a 401, and the app hid it by
 * falling back to a made-up local user.
 *
 * The pairing itself is the one that was already built and already has a
 * revoke button: the phone makes its own key, proves it holds it, the owner's
 * machine confirms, and the phone is handed a token tied to that key. It
 * shows up under Paired devices and revoking it stops working immediately.
 *
 * What is different here is only how the two find each other. There is no QR
 * and no address to travel to, because the phone is already on the other end
 * of the channel. Scanning the code was the owner's gesture; asking them to
 * compare a number with themselves would be theatre.
 *
 * Nothing in this file talks to a network directly. Both halves are handed a
 * way to make a call, so the whole exchange can be driven in a test.
 */

import { generateDeviceKey, pairingChallenge, signChallenge } from "./device-key";

const rest = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * The desktop half: make an offer, send it down the channel, confirm it.
 *
 * @param send  hands one pairing frame to the phone
 * @param call  makes a call against this machine's own backend, signed in
 */
export async function offerPairing({ send, call, wait = rest, tries = 40, every = 500 }) {
  const offer = await call({ method: "POST", path: "/api/devices/pairing", body: { over_channel: true } });
  send({ offer: offer.offer, secret: offer.secret });

  for (let attempt = 0; attempt < tries; attempt += 1) {
    // eslint-disable-next-line no-await-in-loop
    const state = await call({ method: "GET", path: `/api/devices/pairing/${offer.offer}` });
    if (state.state === "claimed") {
      // eslint-disable-next-line no-await-in-loop
      await call({ method: "POST", path: `/api/devices/pairing/${offer.offer}/confirm` });
      return { paired: true, device: state.device_name || "Phone" };
    }
    if (state.state === "confirmed") return { paired: true, device: state.device_name || "Phone" };
    // eslint-disable-next-line no-await-in-loop
    await wait(every);
  }
  return { paired: false };
}

/**
 * The phone half: make a key, claim the offer, wait for the tokens.
 *
 * The private half of the key stays in this browser and is not extractable.
 * All that ever crosses is a public key and a signature.
 */
export async function acceptPairing({
  offer,
  secret,
  call,
  subtle,
  storage,
  name = "Phone",
  wait = rest,
  tries = 40,
  every = 500,
}) {
  const key = await generateDeviceKey(subtle);
  const signature = await signChallenge(
    subtle,
    key.privateKey,
    pairingChallenge(offer, key.publicKeySpkiB64)
  );

  await call({
    method: "POST",
    path: "/api/devices/pairing/claim",
    body: { offer, secret, public_key: key.publicKeySpkiB64, signature, name },
  });

  for (let attempt = 0; attempt < tries; attempt += 1) {
    // eslint-disable-next-line no-await-in-loop
    const state = await call({ method: "POST", path: "/api/devices/pairing/poll", body: { offer, secret } });
    if (state.access_token) {
      // Stored, not reloaded. A reload here would throw away the channel
      // this session is running on, which is the one thing that cannot be
      // rebuilt without walking back to the other room.
      storage.setItem("access_token", state.access_token);
      if (state.refresh_token) storage.setItem("refresh_token", state.refresh_token);
      return true;
    }
    // eslint-disable-next-line no-await-in-loop
    await wait(every);
  }
  return false;
}

/** What to call this phone in the owner's list of devices. */
export function nameFor(userAgent = "") {
  if (/iPhone/i.test(userAgent)) return "iPhone";
  if (/iPad/i.test(userAgent)) return "iPad";
  if (/Android/i.test(userAgent)) return "Android phone";
  return "Phone";
}
